import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiVersions } from './api-version';
import { BaseService } from './base.service';
import { WidgetInstanceModel } from '../models/widget.model';

@Injectable({
  providedIn: 'root'
})
export class WidgetsService extends BaseService {
  private baseUrl = 'widgets';
  private _homepageWidgets: WidgetInstanceModel[] = [];
  public readonly homepageWidgets = new BehaviorSubject<WidgetInstanceModel[]>(null);
  constructor() {
    super();
  }

  public getHomePageWidgets() {
    let path = `${this.baseUrl}/homepage`;
    this.get<WidgetInstanceModel[]>(path, true, ApiVersions.unversioned).subscribe((result) => {
      // A failed request resolves to null here (an error snackbar was already shown);
      // keep the current state rather than blanking the homepage.
      if (result) {
        this.setState(result);
      }
    });
    return this.homepageWidgets;
  }

  // Persists the full ordered list; the backend reconciles inserts/removals and
  // assigns rank by array position, then returns the saved list (with new ids).
  public saveHomePageWidgets(widgets: WidgetInstanceModel[]) {
    let path = `${this.baseUrl}/homepage`;
    this._homepageWidgets = widgets;
    // rethrowError so a failed save hits the error path; the optimistic update in the
    // caller is then rolled back by re-fetching the authoritative server state.
    this.put(path, widgets, null, ApiVersions.unversioned, true).subscribe({
      next: (result: WidgetInstanceModel[]) => this.setState(result),
      error: () => this.getHomePageWidgets()
    });
  }

  // Updates one instance's settings in place and persists the whole list.
  public updateInstanceSettings(instance: WidgetInstanceModel) {
    const index = this._homepageWidgets.findIndex(
      (x) => x === instance || (instance.id != null && x.id === instance.id)
    );
    if (index === -1) {
      // The instance is no longer part of the current homepage state; resync from the
      // server rather than persisting a stale list that would silently drop the change.
      this.getHomePageWidgets();
      return;
    }
    this._homepageWidgets[index].settings = instance.settings;
    this.saveHomePageWidgets(this._homepageWidgets);
  }

  private setState(widgets: WidgetInstanceModel[]) {
    this._homepageWidgets = (widgets ?? []).slice().sort((a, b) => a.rank - b.rank);
    this.homepageWidgets.next(this._homepageWidgets);
  }
}
